import { useMemo } from 'react';
import { CalendarOff, CalendarCheck2, CalendarClock, CalendarX2 } from 'lucide-react';
import { Chip } from '@vnetwork/ui/primitives';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { useMyDayOffs, useDayOffSummary, rangeDate, type MyDayOff } from '@/api/account-lists';
import { useCurrentEmployee } from '@/api/account';
import { formatDate } from '@/lib/format';
import { statusTone } from './components/status-tone';
import { AccountListScaffold, type ScaffoldColumn } from './components/AccountListScaffold';
import { AccountStatCards } from './components/AccountStatCards';

const typeLabel = (s: string) => (s ? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '—');
const STATUS_OPTIONS = ['approved', 'leader-approved', 'pending', 'rejected', 'cancelled'].map((s) => ({ value: s, label: typeLabel(s) }));

function range(d: MyDayOff): string {
  const dates = (d.day_off_range_time ?? []).map(rangeDate).filter(Boolean).sort();
  if (dates.length === 0) return '—';
  const first = formatDate(dates[0]);
  const last = formatDate(dates[dates.length - 1]);
  return first === last ? first : `${first} → ${last}`;
}

/** Day-off Accounts — leave summary + filterable list (mirrors legacy NGHỈ PHÉP). */
export default function DayoffsPage() {
  const { data, isLoading, isError, refetch } = useMyDayOffs({ page: 1, page_size: 50 });
  const { data: emp } = useCurrentEmployee();
  const { data: summary } = useDayOffSummary();
  const rows = data?.results ?? [];

  const typeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.day_off_type).filter(Boolean))).map((t) => ({ value: t, label: typeLabel(t) })),
    [rows],
  );

  const banner = (
    <AccountStatCards
      items={[
        { icon: <CalendarCheck2 size={16} />, label: 'Leave remaining', value: emp?.employee_day_off_left ?? '—', tone: 'var(--vn-success-solid)' },
        { icon: <CalendarClock size={16} />, label: 'Pending approval', value: summary?.pending_total ?? '—', tone: 'var(--vn-warning-solid)' },
        { icon: <CalendarX2 size={16} />, label: 'Rejected', value: summary?.rejected_total ?? '—', tone: 'var(--vn-danger-solid)' },
      ]}
    />
  );

  const columns = useMemo<ScaffoldColumn<MyDayOff>[]>(() => [
    { id: 'id', header: 'ID', accessorKey: 'day_off_id', cell: ({ getValue }) => String(getValue()) },
    { id: 'type', header: 'Type', cell: ({ row }) => typeLabel(row.original.day_off_type) },
    { id: 'dates', header: 'Dates', cell: ({ row }) => range(row.original) },
    { id: 'days', header: 'Days', cell: ({ row }) => String(row.original.number_of_day_off ?? 0) },
    { id: 'reason', header: 'Reason', accessorKey: 'day_off_reason', cell: ({ getValue }) => String(getValue() ?? '—') },
    {
      id: 'approvals', header: 'Approvals',
      cell: ({ row }) => (
        <div style={{ display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
          <Chip size="sm" tone={row.original.day_off_leader_confirmed ? 'success' : 'neutral'}>Leader</Chip>
          <Chip size="sm" tone={row.original.day_off_bod_confirmed ? 'success' : 'neutral'}>BOD</Chip>
        </div>
      ),
    },
    { id: 'created', header: 'Created', cell: ({ row }) => formatDate(row.original.day_off_created_at) },
    {
      id: 'status', header: 'Status',
      cell: ({ row }) => <StatusPill tone={statusTone(row.original.day_off_status)}>{row.original.day_off_status || '—'}</StatusPill>,
    },
  ], []);

  return (
    <AccountListScaffold
      title="Day-off Accounts"
      breadcrumbLabel="Day-offs"
      description="Your leave requests"
      banner={banner}
      searchPlaceholder="Search day-offs"
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      getRowId={(r) => String(r.day_off_id)}
      columns={columns}
      searchText={(r) => `${r.day_off_id} ${typeLabel(r.day_off_type)} ${r.day_off_reason} ${range(r)}`}
      filterFields={[
        { value: 'type', label: 'Type', input: 'select', options: typeOptions },
        { value: 'status', label: 'Status', input: 'select', options: STATUS_OPTIONS },
        { value: 'reason', label: 'Reason' },
      ]}
      filterAccessor={(r, field) =>
        field === 'type' ? r.day_off_type : field === 'status' ? r.day_off_status : field === 'reason' ? r.day_off_reason : ''
      }
      emptyState={<EmptyState icon={<CalendarOff size={24} />} title="No day-off requests" />}
    />
  );
}
